import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

interface NetworkContextValue {
  isOffline: boolean;
  retry: () => void;
}

const NetworkContext = createContext<NetworkContextValue | null>(null);

const RETRY_INTERVAL_MS = 30_000;

export function NetworkProvider({ children }: { children: ReactNode }) {
  const [isOffline, setIsOffline] = useState(false);

  const checkHealth = useCallback(async () => {
    try {
      const res = await fetch('/api/health', { method: 'GET' });
      if (res.ok) {
        setIsOffline(false);
      }
    } catch {
      // Still offline
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      checkHealth();
    };
    const handleOffline = () => setIsOffline(true);
    const handleNetworkError = () => setIsOffline(true);
    const handleNetworkOk = () => setIsOffline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('network-error', handleNetworkError);
    window.addEventListener('network-ok', handleNetworkOk);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('network-error', handleNetworkError);
      window.removeEventListener('network-ok', handleNetworkOk);
    };
  }, [checkHealth]);

  // Auto-retry when offline
  useEffect(() => {
    if (!isOffline) return;
    const id = setInterval(checkHealth, RETRY_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isOffline, checkHealth]);

  return (
    <NetworkContext.Provider value={{ isOffline, retry: checkHealth }}>
      {children}
    </NetworkContext.Provider>
  );
}

export function useNetwork() {
  const ctx = useContext(NetworkContext);
  if (!ctx) throw new Error('useNetwork must be used within NetworkProvider');
  return ctx;
}
